import { Box, SxProps, Theme, Typography } from "@mui/material";
import { useMemo } from "react";
import { BrandLogo } from "./InternBrandStyleLogoItem";

type LogoProps = {
  logo?: BrandLogo | string;
  size?: "small" | "large";
};

const MAX_HEIGHT = 150;
const PADDING = 8;

// Scraped SVG markup is untrusted; loading it through <img> keeps its scripts and event handlers from running.
// It is an HTML fragment, so it is re-serialized as standalone XML (namespaces declared, entities resolved)
// for image/svg+xml to accept it. The parsed document is inert: nothing in it executes or loads.
const svgDataUrl = (html: string) => {
  const svg = new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector("svg");
  if (!svg) return undefined;
  // The serializer declares namespaces itself; literal xmlns attributes would be emitted twice.
  [svg, ...svg.querySelectorAll("*")].forEach((el) =>
    el.removeAttribute("xmlns")
  );
  const xml = new XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
};

const InternLogoImage = ({ logo }: Required<Pick<LogoProps, "logo">>) => {
  const logoUrl = useMemo(
    () => (typeof logo === "string" ? logo : logo.src || svgDataUrl(logo.html)),
    [logo]
  );
  return (
    <img
      src={logoUrl}
      alt="Logo"
      style={{
        maxWidth: "100%",
        // The container's height is indefinite, so a percentage would not limit the image.
        maxHeight: MAX_HEIGHT - 2 * PADDING,
        objectFit: "contain",
      }}
    />
  );
};

const InternLogo = ({ logo, size = "small" }: LogoProps) => {
  const backgroundStyles: SxProps<Theme> = useMemo(() => {
    if (logo) {
      return {};
    }

    return {
      backgroundColor: "white",
    };
  }, [logo]);

  return (
    <Box
      sx={{
        width: size === "large" ? 200 : 100,
        maxHeight: MAX_HEIGHT,
        minHeight: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        p: `${PADDING}px`,
        ...backgroundStyles,
      }}
    >
      {logo ? (
        <InternLogoImage logo={logo} />
      ) : (
        <div>
          <Typography variant="body1" color="text.secondary">
            No logo
          </Typography>
        </div>
      )}
    </Box>
  );
};

export default InternLogo;
